import React from "react";
import { Text, View } from "@react-pdf/renderer";
import { withNamespaces } from "react-i18next";

import { formatOwnershipPercentage } from "../../helpers/groupStructureGraph";

const NODE_COLORS = {
    shareholder: {
        backgroundColor: "#388e3c",
        borderColor: "#4caf50",
    },
    main: {
        backgroundColor: "#1976d2",
        borderColor: "#1976d2",
    },
    subsidiary: {
        backgroundColor: "#ff9800",
        borderColor: "#f57c00",
    },
};

const GraphNode = ({ label, nodeType }) => {
    const colors = NODE_COLORS[nodeType] || NODE_COLORS.main;

    return (
        <View
            style={{
                backgroundColor: colors.backgroundColor,
                borderColor: colors.borderColor,
                borderWidth: 2,
                borderRadius: 6,
                paddingVertical: 5,
                paddingHorizontal: 4,
                minHeight: 34,
                justifyContent: "center",
                width: "100%",
            }}
        >
            <Text
                style={{
                    fontSize: 6,
                    color: "#FFFFFF",
                    textAlign: "center",
                    lineHeight: 1.2,
                }}
            >
                {label}
            </Text>
        </View>
    );
};

const ConnectorLine = ({ height = 12 }) => (
    <View
        style={{
            width: 1,
            height,
            backgroundColor: "#555555",
        }}
    />
);

const HorizontalConnector = ({ width = "72%" }) => (
    <View
        style={{
            width,
            height: 1,
            backgroundColor: "#555555",
            alignSelf: "center",
        }}
    />
);

const GraphRow = ({ nodes = [], nodeType, showPercentageAbove = false, showPercentageBelow = false }) => {
    if (!nodes.length) {
        return null;
    }

    const cellWidth = `${100 / nodes.length}%`;

    return (
        <View style={{ flexDirection: "row", justifyContent: "space-around", width: "100%" }}>
            {nodes.map((node) => (
                <View
                    key={node.cin}
                    style={{
                        width: cellWidth,
                        paddingHorizontal: 2,
                        alignItems: "center",
                    }}
                >
                    {showPercentageAbove && (
                        <Text style={styles.percentageText}>
                            {formatOwnershipPercentage(node.percentage) || " "}
                        </Text>
                    )}

                    <GraphNode label={node.name} nodeType={nodeType} />

                    {showPercentageBelow && (
                        <Text style={styles.percentageText}>
                            {formatOwnershipPercentage(node.percentage) || " "}
                        </Text>
                    )}
                </View>
            ))}
        </View>
    );
};

const GroupStructureGraph = ({ t, graphData }) => {
    const shareholders = graphData?.shareholders || [];
    const subsidiaries = graphData?.subsidiaries || [];
    const main = graphData?.main || { name: t("No Data") };

    const hasRelations = shareholders.length > 0 || subsidiaries.length > 0;

    return (
        <View style={styles.container}>
            {shareholders.length > 0 && (
                <View style={styles.layer}>
                    <GraphRow
                        nodes={shareholders}
                        nodeType="shareholder"
                        showPercentageBelow
                    />

                    <View style={styles.connectorColumn}>
                        <HorizontalConnector />
                        <ConnectorLine height={10} />
                    </View>
                </View>
            )}

            <View style={styles.mainLayer}>
                {shareholders.length > 0 && (
                    <View style={styles.shareholderBranchRow}>
                        {shareholders.map((shareholder) => (
                            <View
                                key={`branch-${shareholder.cin}`}
                                style={{
                                    width: `${100 / shareholders.length}%`,
                                    alignItems: "center",
                                }}
                            >
                                <ConnectorLine height={8} />
                            </View>
                        ))}
                    </View>
                )}

                <View style={styles.mainNodeWrap}>
                    <GraphNode label={main.name} nodeType="main" />
                </View>
            </View>

            {subsidiaries.length > 0 && (
                <View style={styles.layer}>
                    <View style={styles.connectorColumn}>
                        <ConnectorLine height={10} />
                        <HorizontalConnector />
                    </View>

                    <View style={styles.subsidiaryBranchRow}>
                        {subsidiaries.map((subsidiary) => (
                            <View
                                key={`branch-${subsidiary.cin}`}
                                style={{
                                    width: `${100 / subsidiaries.length}%`,
                                    alignItems: "center",
                                }}
                            >
                                <ConnectorLine height={8} />
                            </View>
                        ))}
                    </View>

                    <GraphRow
                        nodes={subsidiaries}
                        nodeType="subsidiary"
                        showPercentageAbove
                    />
                </View>
            )}

            {!hasRelations && (
                <Text style={styles.emptyHint}>
                    {t("No ownership relations available")}
                </Text>
            )}
        </View>
    );
};

const styles = {
    container: {
        borderWidth: 1,
        borderColor: "#B0B0B0",
        borderStyle: "dashed",
        paddingVertical: 12,
        paddingHorizontal: 8,
        marginBottom: 10,
        minHeight: 120,
    },
    layer: {
        width: "100%",
    },
    mainLayer: {
        alignItems: "center",
        marginVertical: 4,
    },
    mainNodeWrap: {
        width: 220,
        maxWidth: "70%",
    },
    connectorColumn: {
        width: "100%",
        alignItems: "center",
        marginVertical: 2,
    },
    shareholderBranchRow: {
        flexDirection: "row",
        width: "100%",
        marginBottom: 2,
    },
    subsidiaryBranchRow: {
        flexDirection: "row",
        width: "100%",
        marginTop: 2,
        marginBottom: 2,
    },
    percentageText: {
        fontSize: 5,
        color: "#333333",
        fontWeight: 600,
        textAlign: "center",
        marginVertical: 2,
        minHeight: 8,
    },
    emptyHint: {
        fontSize: 7,
        color: "#6B7280",
        textAlign: "center",
        marginTop: 6,
        fontStyle: "italic",
    },
};

export default withNamespaces()(GroupStructureGraph);
